import React, { useState, useEffect } from 'react';
import { config } from '../../config';
import StorageStatus from './StorageStatus';
import './scss/AppSumoTab.scss';

const AppSumoTab = ({ shortId }) => {
    const [code, setCode] = useState('');
    const [validationResult, setValidationResult] = useState(null);
    const [isValidating, setIsValidating] = useState(false);
    const [isRedeeming, setIsRedeeming] = useState(false);
    const [redeemResult, setRedeemResult] = useState(null);
    const [error, setError] = useState('');
    const [workspaceEligibility, setWorkspaceEligibility] = useState(null);

    const tiers = [
        {
            key: 'tier1',
            name: 'Tier 1',
            seats: 10,
            storage: 5, // GB
            whiteLabel: false,
            price: 99,
            features: {
                core: [
                    'Lifetime access',
                    '1 Pro Workspace',
                    '10 team seats',
                    '5GB storage'
                ],
                boards: [
                    'Boards: 25',
                    'Unlimited cards',
                    'File attachments: 250 MB per file',
                    'Advanced checklists',
                    'Saved searches',
                    'Board backgrounds',
                    'Public board visibility controls',
                    'Task timers (stopwatch & countdown)'
                ],
                analytics: [
                    'Basic board analytics',
                    'Activity tracking'
                ],
                collaboration: [
                    'User role management',
                    'Team member management',
                    'Last activity status for all members',
                    'Online status visibility',
                    'Pro verified badge'
                ],
                integrations: [
                    'Trello import',
                    'FluentBoards import',
                    'Unsplash integration'
                ]
            }
        },
        {
            key: 'tier2',
            name: 'Tier 2',
            seats: 20,
            storage: 10, // GB
            whiteLabel: false,
            price: 199,
            features: {
                core: [
                    'Lifetime access',
                    '1 Pro Workspace',
                    '20 team seats',
                    '10GB storage'
                ],
                boards: [
                    'Boards: 50',
                    'Unlimited cards',
                    'File attachments: 250 MB per file',
                    'Advanced checklists',
                    'Saved searches',
                    'Board backgrounds',
                    'Public board visibility controls',
                    'Task timers (stopwatch & countdown)'
                ],
                analytics: [
                    'board analytics',
                    'User productivity metrics',
                    'Performance insights',
                    'Activity tracking',
                    'Extended activity logs',
                ],
                collaboration: [
                    'User role management',
                    'Team member management',
                    'Last activity status for all members',
                    'Online status visibility',
                    'Pro verified badge',
                    'Priority support'
                ],
                integrations: [
                    'Trello import',
                    'FluentBoards import',
                    'Unsplash integration',
                    'Advanced search capabilities'
                ],
            }
        },
        {
            key: 'tier3',
            name: 'Tier 3',
            seats: 30,
            storage: 20, // GB
            whiteLabel: true,
            price: 299,
            features: {
                core: [
                    'Lifetime access',
                    '1 Pro Workspace',
                    '30 team seats',
                    '20GB storage'
                ],
                boards: [
                    'Boards: Unlimited',
                    'Unlimited cards',
                    'File attachments: 250 MB per file',
                    'Advanced checklists',
                    'Saved searches',
                    'Board backgrounds',
                    'Public board visibility controls',
                    'Task timers (stopwatch & countdown)'
                ],
                analytics: [
                    'Advanced board analytics',
                    'User productivity metrics',
                    'Performance insights',
                    'Activity tracking',
                    'Extended activity logs',
                    'Analytics data export'
                ],
                collaboration: [
                    'User role management',
                    'Team member management',
                    'Last activity status for all members',
                    'Online status visibility',
                    'Pro verified badge',
                    'Priority support'
                ],
                integrations: [
                    'Trello import',
                    'FluentBoards import',
                    'Unsplash integration',
                    'Advanced search capabilities'
                ],
                exclusive: [
                    'Lifetime white-label branding (Tier 3 Exclusive)',
                    'Custom domain support',
                    'Complete brand customization',
                    'Remove Zoobbe branding'
                ]
            }
        }
    ];




    // Fetch AppSumo tiers and check workspace eligibility on component mount
    useEffect(() => {


        const checkWorkspaceEligibility = async () => {
            try {
                const response = await fetch(`${config.API_URI}/v1/appsumo/workspace/${shortId}/storage`, {
                    credentials: 'include'
                });
                if (response.ok) {
                    const data = await response.json();
                    setWorkspaceEligibility({
                        canRedeemAppSumo: !data.subscription || data.subscription.planType === 'Free' || data.subscription.planType === 'AppSumo',
                        currentPlan: data.subscription?.planType || 'Free',
                        currentTier: data.subscription?.appSumo?.tier
                    });
                } else {
                    // If we can't check, assume it's eligible (fallback)
                    setWorkspaceEligibility({ canRedeemAppSumo: true, currentPlan: 'Unknown' });
                }
            } catch (error) {
                console.error('Error checking workspace eligibility:', error);
                setWorkspaceEligibility({ canRedeemAppSumo: true, currentPlan: 'Unknown' });
            }
        };

        checkWorkspaceEligibility();
    }, [shortId]);

    const handleValidateCode = async () => {
        if (!code.trim()) {
            setError('Please enter a code');
            return;
        }

        setIsValidating(true);
        setError('');
        setValidationResult(null);

        try {
            const response = await fetch(`${config.API_URI}/v1/appsumo/validate-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    code: code.trim(),
                    workspaceId: shortId
                })
            });

            const data = await response.json();

            if (response.ok && data.valid) {
                setValidationResult(data);
            } else {
                setError(data.message || 'Invalid code');
            }
        } catch (error) {
            setError('Error validating code');
            console.error('Error validating code:', error);
        } finally {
            setIsValidating(false);
        }
    };

    const handleRedeemCode = async () => {
        if (!validationResult) return;

        setIsRedeeming(true);
        setError('');

        try {
            const response = await fetch(`${config.API_URI}/v1/appsumo/redeem`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    code: code.trim(),
                    workspaceId: shortId
                })
            });

            const data = await response.json();

            if (response.ok) {
                setRedeemResult(data);
                setValidationResult(null);
                setCode('');
            } else {
                setError(data.message || 'Error redeeming code');
            }
        } catch (error) {
            setError('Error redeeming code');
            console.error('Error redeeming code:', error);
        } finally {
            setIsRedeeming(false);
        }
    };

    const resetForm = () => {
        setCode('');
        setValidationResult(null);
        setError('');
        setRedeemResult(null);
    };

    return (
        <div className="appsumo-tab">
            <div className="appsumo-header">
                <h2>AppSumo Lifetime Deal</h2>
                <p>Unlock lifetime access to Zoobbe with your AppSumo code</p>
            </div>

            {/* Storage Status for existing AppSumo users */}
            <StorageStatus workspaceId={shortId} />

            {/* Workspace Eligibility Check */}
            {workspaceEligibility && !workspaceEligibility.canRedeemAppSumo && (
                <div className="ineligible-workspace">
                    <div className="warning-icon">⚠️</div>
                    <h3>AppSumo Code Redemption Not Available</h3>
                    <p>
                        This workspace is currently on a <strong>{workspaceEligibility.currentPlan}</strong> plan.
                        AppSumo codes can only be redeemed on Free workspaces or to upgrade existing AppSumo tiers.
                    </p>
                    <div className="migration-info">
                        <h4>Want to use AppSumo instead?</h4>
                        <p>You can:</p>
                        <ul>
                            <li>Create a new workspace and redeem your AppSumo code there</li>
                            <li>Contact support to discuss migration options</li>
                            <li>Keep your current {workspaceEligibility.currentPlan} plan for unlimited features</li>
                        </ul>
                    </div>
                </div>
            )}

            {/* Success Message */}
            {redeemResult && (
                <div className="success-message">
                    <div className="success-icon">✅</div>
                    <h3>Code Redeemed Successfully!</h3>
                    <p>Your workspace has been upgraded to {redeemResult.subscription.tier} with {redeemResult.subscription.seats} seats.</p>
                    <button onClick={resetForm} className="new-code-btn">Redeem Another Code</button>
                </div>
            )}

            {/* Code Input Section */}
            {!redeemResult && workspaceEligibility && workspaceEligibility.canRedeemAppSumo && (
                <div className="code-input-section">
                    <div className="input-group">
                        <input
                            type="text"
                            placeholder="Enter your AppSumo code"
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            className="code-input"
                            disabled={isValidating || isRedeeming}
                        />
                        <button
                            onClick={handleValidateCode}
                            disabled={!code.trim() || isValidating || isRedeeming}
                            className="validate-btn"
                        >
                            {isValidating ? 'Validating...' : 'Validate Code'}
                        </button>
                    </div>

                    {error && <div className="error-message">{error}</div>}

                    {/* Validation Result */}
                    {validationResult && (
                        <div className="validation-result">
                            <div className="valid-code">
                                <div className="check-icon">✅</div>
                                <div className="code-details">
                                    <h4>Valid Code - {tiers[validationResult.tier]?.name}</h4>
                                    <p>This code will give you:</p>
                                    <div className="tier-preview">
                                        <div className="core-features">
                                            <strong>Core:</strong>
                                            <ul>
                                                <li>{validationResult.tierSpecs.seats} seats</li>
                                                <li>{validationResult.tierSpecs.storage}GB storage</li>
                                                <li>Lifetime access</li>
                                                {validationResult.tierSpecs.whiteLabel && <li>White label branding</li>}
                                            </ul>
                                        </div>
                                        <div className="feature-highlights">
                                            <strong>Includes:</strong>
                                            <ul>
                                                <li>🤖 AI features (limited usage)</li>
                                                <li>📊 Advanced analytics</li>
                                                <li>⏱️ Time tracking & timers</li>
                                                <li>🔗 Import/export tools</li>
                                                {validationResult.tier >= 2 && <li>🔒 Enhanced security</li>}
                                                {validationResult.tier === 3 && <li>⭐ White label branding</li>}
                                            </ul>
                                        </div>
                                    </div>
                                    <p className="feature-count">
                                        + {Object.values(tiers[validationResult.tier]?.features || {}).flat().length} total features
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleRedeemCode}
                                disabled={isRedeeming}
                                className="redeem-btn"
                            >
                                {isRedeeming ? 'Redeeming...' : 'Redeem Code'}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* AppSumo Tiers Display */}
            <div className="appsumo-tiers">
                <div className="tiers-header">
                    <h3>Available Tiers</h3>
                    <button
                        className="compare-features-btn"
                        onClick={() => window.open('/features-comparison', '_blank')}
                    >
                        Compare All Features
                    </button>
                </div>
                <div className="tiers-grid">
                    {tiers?.map((tier, idx) => (
                        <div key={idx} className={`tier-card ${tier.key}`}>
                            <div className="tier-header">
                                <h4>{tier.name}</h4>
                                <div className="tier-price">${tier.price}</div>
                                <div className="tier-subtitle">Lifetime Deal</div>
                            </div>
                            <div className="tier-features">
                                {/* Core Features */}
                                <div className="feature-category">
                                    <h5>Core Features</h5>
                                    <ul>
                                        {tier.features.core.map((feature, fIdx) => (
                                            <li key={fIdx}>
                                                <span className="check">✓</span>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* AI Features */}
                                <div className="feature-category">
                                    <h5>🗂️ Boards</h5>
                                    <ul>
                                        {tier.features.boards.map((feature, fIdx) => (
                                            <li key={fIdx}>
                                                <span className="check">✓</span>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Analytics */}
                                <div className="feature-category">
                                    <h5>📊 Analytics & Insights</h5>
                                    <ul>
                                        {tier.features.analytics.map((feature, fIdx) => (
                                            <li key={fIdx}>
                                                <span className="check">✓</span>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="feature-category">
                                    <h5>👥 Collaboration</h5>
                                    <ul>
                                        {tier.features.collaboration.map((feature, fIdx) => (
                                            <li key={fIdx}>
                                                <span className="check">✓</span>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="feature-category">
                                    <h5>🔗 Integrations</h5>
                                    <ul>
                                        {tier.features.integrations.map((feature, fIdx) => (
                                            <li key={fIdx}>
                                                <span className="check">✓</span>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Show more categories for higher tiers */}
                                {/* {tier.key !== 'tier1' && (
                                    <>
                                        <div className="feature-category">
                                            <h5>🔒 Security & Management</h5>
                                            <ul>
                                                {tier.features.security?.map((feature, fIdx) => (
                                                    <li key={fIdx}>
                                                        <span className="check">✓</span>
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </>
                                )} */}

                                {/* Exclusive features for Tier 3 */}
                                {tier.key === 'tier3' && (
                                    <div className="feature-category exclusive">
                                        <h5>⭐ Tier 3 Exclusive</h5>
                                        <ul>
                                            {tier.features.exclusive.map((feature, fIdx) => (
                                                <li key={fIdx}>
                                                    <span className="check exclusive">✓</span>
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="feature-summary">
                                    <small>+ All free features</small>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="appsumo-info">
                <h4>How it works:</h4>
                <ol>
                    <li>Purchase an AppSumo lifetime deal</li>
                    <li>Enter your unique code above</li>
                    <li>Validate and redeem the code for this workspace</li>
                    <li>Enjoy lifetime access to Zoobbe!</li>
                </ol>
            </div>
        </div>
    );
};

export default AppSumoTab;
